import React from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';

const AlgemeneVoorwaarden: React.FC = () => {
  const navigate = useNavigate();
  usePageTitle('Algemene Voorwaarden');

  return (
    <div className="min-h-screen">
      <div
        className="relative py-20"
        style={{
          backgroundImage: `url('/hero-background.webp')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="container mx-auto px-8">
          <button
            onClick={() => navigate('/')}
            className="mb-6 text-white hover:text-orange-400 flex items-center transition"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Terug naar Home
          </button>
          <h1 className="text-5xl font-bold text-white mb-4">Algemene Voorwaarden</h1>
          <p className="text-xl text-white/90 max-w-3xl">
            Laatste update: Mei 2026
          </p>
        </div>
      </div>

      <div className="container mx-auto px-8 py-16">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg p-8">
          <section className="mb-8">
            <p className="text-gray-700 leading-relaxed">
              Deze algemene voorwaarden zijn van toepassing op alle offertes, opdrachten en overeenkomsten
              tussen SnapLogic en haar opdrachtgevers met betrekking tot het ontwerpen, ontwikkelen en
              opleveren van websites en mobiele applicaties, evenals aanvullende diensten zoals SEO,
              advertentie-integraties en koppelingen met externe systemen.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">1. Definities</h2>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>SnapLogic:</strong> de opdrachtnemer, gevestigd in 's-Gravenhage.</li>
              <li><strong>Opdrachtgever:</strong> de natuurlijke of rechtspersoon die met SnapLogic een overeenkomst aangaat.</li>
              <li><strong>Configurator:</strong> de online tool op deze website waarmee een indicatief pakket en richtprijs worden samengesteld.</li>
              <li><strong>Offerte:</strong> het schriftelijke aanbod dat na intake aan opdrachtgever wordt uitgebracht.</li>
              <li><strong>Opdracht:</strong> de tussen partijen tot stand gekomen overeenkomst tot levering van werkzaamheden.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">2. Toepasselijkheid</h2>
            <p className="text-gray-700">
              Deze voorwaarden zijn van toepassing op iedere aanbieding, offerte en overeenkomst tussen
              SnapLogic en opdrachtgever, tenzij schriftelijk uitdrukkelijk anders is overeengekomen.
              Algemene (inkoop)voorwaarden van opdrachtgever worden uitdrukkelijk van de hand gewezen.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">3. Configurator en offertes</h2>
            <p className="text-gray-700 mb-3">
              De prijs die in de online configurator wordt getoond is een richtprijs op basis van de door
              opdrachtgever gemaakte selecties (pakket, doorlooptijd en gekozen uitbreidingen). Hieraan
              kunnen geen rechten worden ontleend. Na ontvangst van een aanvraag verstrekt SnapLogic een
              schriftelijke offerte op basis van een korte intake.
            </p>
            <p className="text-gray-700">
              Offertes zijn 30 dagen geldig, tenzij anders aangegeven. Een overeenkomst komt tot stand
              wanneer opdrachtgever de offerte schriftelijk (waaronder per e-mail) bevestigt.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">4. Prijzen en spoedtoeslagen</h2>
            <p className="text-gray-700 mb-3">
              Alle bedragen zijn in euro's en exclusief BTW, tenzij uitdrukkelijk anders vermeld. Op
              kortere doorlooptijden geldt een toeslag overeenkomstig de configurator:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Standaard doorlooptijd (circa 1 maand): geen toeslag.</li>
              <li>Versnelde oplevering (circa 2 weken): +25% toeslag op alle posten.</li>
              <li>Super snelle oplevering (circa 1 week): +50% toeslag op alle posten.</li>
            </ul>
            <p className="text-gray-700 mt-3">
              Genoemde doorlooptijden zijn streeftijden en gaan in nadat opdrachtgever alle voor de start
              benodigde content, toegangen en goedkeuringen heeft aangeleverd.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">5. Betaling</h2>
            <p className="text-gray-700 mb-3">
              Tenzij anders overeengekomen factureert SnapLogic in twee termijnen: 50% bij aanvang van de
              opdracht en 50% bij oplevering. Bij projecten met een langere doorlooptijd of grotere
              omvang kunnen aanvullende termijnen worden afgesproken.
            </p>
            <p className="text-gray-700">
              Facturen dienen binnen 14 dagen na factuurdatum te zijn voldaan. Bij niet-tijdige betaling
              is opdrachtgever van rechtswege in verzuim en is wettelijke (handels)rente verschuldigd,
              evenals eventuele buitengerechtelijke incassokosten.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">6. Uitvoering, oplevering en acceptatie</h2>
            <p className="text-gray-700 mb-3">
              SnapLogic spant zich in om de opdracht zorgvuldig en vakbekwaam uit te voeren. Opdrachtgever
              draagt zorg voor tijdige aanlevering van benodigde materialen (teksten, beeldmateriaal,
              merkrichtlijnen, toegangen) en voor tijdige feedback op tussentijdse opleveringen.
            </p>
            <p className="text-gray-700">
              Na oplevering heeft opdrachtgever 7 kalenderdagen om gebreken schriftelijk te melden.
              Binnen die periode worden redelijke correcties verwerkt binnen de oorspronkelijke scope.
              Zonder reactie binnen deze termijn wordt de oplevering geacht te zijn geaccepteerd.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">7. Meerwerk en wijzigingen</h2>
            <p className="text-gray-700">
              Wijzigingen in de opdracht na bevestiging van de offerte (zoals extra pagina's,
              functionaliteiten, integraties of een gewijzigde doorlooptijd) worden als meerwerk
              beschouwd. SnapLogic verstrekt vooraf een schriftelijke opgave van de meerprijs en de
              gevolgen voor de planning. Werkzaamheden worden pas voortgezet na akkoord.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">8. Intellectueel eigendom</h2>
            <p className="text-gray-700 mb-3">
              Na volledige betaling van het overeengekomen bedrag verkrijgt opdrachtgever een
              gebruiksrecht op de opgeleverde website of applicatie ten behoeve van het eigen bedrijf.
              Broncode, ontwerpbestanden en andere werkresultaten worden op verzoek beschikbaar gesteld.
            </p>
            <p className="text-gray-700">
              Rechten op door derden geleverde componenten (zoals open source bibliotheken, lettertypen,
              afbeeldingen of plug-ins) blijven berusten bij de oorspronkelijke rechthebbenden en zijn
              onderhevig aan hun eigen licentievoorwaarden.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">9. Hosting, onderhoud en derden</h2>
            <p className="text-gray-700">
              Hosting, domeinregistratie en doorlopend onderhoud maken geen onderdeel uit van de
              standaard opdracht, tenzij dit expliciet in de offerte is opgenomen. SnapLogic is niet
              aansprakelijk voor storingen, prijswijzigingen of beëindiging van diensten van derden
              (zoals hostingpartijen, betaalproviders, advertentienetwerken of app stores).
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">10. Aansprakelijkheid</h2>
            <p className="text-gray-700 mb-3">
              De aansprakelijkheid van SnapLogic is beperkt tot het bedrag dat in het kader van de
              betreffende opdracht aan opdrachtgever in rekening is gebracht in de drie maanden
              voorafgaand aan de schadeveroorzakende gebeurtenis.
            </p>
            <p className="text-gray-700">
              SnapLogic is nimmer aansprakelijk voor indirecte schade, waaronder gevolgschade, gederfde
              winst, gemiste besparingen, reputatieschade of schade door verlies van data. De beperkingen
              gelden niet bij opzet of bewuste roekeloosheid van SnapLogic.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">11. Overmacht</h2>
            <p className="text-gray-700">
              In geval van overmacht is SnapLogic gerechtigd de uitvoering van de opdracht op te
              schorten zonder tot enige schadevergoeding gehouden te zijn. Onder overmacht wordt onder
              meer verstaan: ziekte van sleutelpersoneel, storingen bij toeleveranciers, internet- of
              stroomstoringen en gewijzigde wet- of regelgeving.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">12. Geheimhouding</h2>
            <p className="text-gray-700">
              Partijen behandelen alle informatie die zij in het kader van de opdracht van elkaar
              ontvangen vertrouwelijk, voor zover deze informatie redelijkerwijs als vertrouwelijk kan
              worden aangemerkt. Deze verplichting geldt ook na beëindiging van de opdracht.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">13. Beëindiging</h2>
            <p className="text-gray-700">
              Indien opdrachtgever de opdracht tussentijds beëindigt, is het reeds uitgevoerde werk
              verschuldigd, evenals reeds gemaakte kosten voor licenties, hosting en diensten van
              derden. SnapLogic behoudt zich het recht voor de opdracht op te schorten of te ontbinden
              bij ernstige tekortkoming, faillissement of surseance van opdrachtgever.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">14. Klachten</h2>
            <p className="text-gray-700">
              Klachten over de uitvoering van de opdracht dienen binnen 14 dagen na ontdekking, doch
              uiterlijk binnen 30 dagen na oplevering, schriftelijk aan SnapLogic kenbaar te worden
              gemaakt. SnapLogic reageert binnen 14 dagen met een voorstel voor herstel of oplossing.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">15. Toepasselijk recht en geschillen</h2>
            <p className="text-gray-700">
              Op alle overeenkomsten tussen SnapLogic en opdrachtgever is uitsluitend Nederlands recht
              van toepassing. Geschillen die niet in onderling overleg kunnen worden opgelost, worden
              voorgelegd aan de bevoegde rechter in het arrondissement Den Haag.
            </p>
          </section>

          <section className="bg-blue-50 p-6 rounded-xl">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">Contact</h2>
            <p className="text-gray-700 mb-4">
              Heeft u vragen over deze voorwaarden? Neem dan contact met ons op:
            </p>
            <div className="text-gray-700">
              <p className="mb-2"><strong>SnapLogic</strong></p>
              <p className="mb-2">📧 E-mail: info@snaplogic.nl</p>
              <p className="mb-2">📱 Telefoon: 06-10654827</p>
              <p className="mb-2">📍 Adres: Cornelis van der Lijnstraat 230, 2593 NP 's-Gravenhage</p>
              <p>🏢 KvK: 97231762</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default AlgemeneVoorwaarden;
